import { useEffect, useState } from "react";
import { CheckmarkIcon, ErrorIcon } from "react-hot-toast";
import { ArrowDownTrayIcon } from "@heroicons/react/24/solid";
import { useOutletContext, useParams } from "react-router-dom";

import { getFormattedSectionData } from "./selectors";
import { Accordion } from "../../components/atomic/Accordion";
import GetAuthContext from "../../shared/context/AuthContext";
import { LoanApplicationService } from "../../services/LoanApplication";
import ActivityTimeline from "../../components/atomic/ActivityTimeline";
import MobileDetailsSection from "../../components/composed/MobileDetailsSection";
import { MOBILE_LEADS_AGENT_DETAILS_SECTION } from "../../services/Leads/constants";
import DetailsSection from "../../components/composed/DetailsSection/DetailsSection";
import LoanDetailsTab from "../../../unified-dashboard/src/pages/LoanDetailsTab";

import {
  MOBILE_LOAN_DETAILS_SECTION,
  MOBILE_LOAN_DETAILS_SECTION_LINKS
} from "../../services/LoanApplication/constants";
import { notify } from "src/shared/context/AlertContext";
import { LOAN_DETAIL_FIELDS } from "@unified-dashboard/src/services/LoanApplication/constants";
import { ORG_IDS } from "@unified-dashboard/src/shared/utils/constants";

function LoanOverview({ pageName }: { pageName?: string }) {
  const { loanDetails, whiteLabelStyle } = useOutletContext<any>();
  const { fetchToken, userState, getPageConfig, getDefaultConfig } =
    GetAuthContext() as any;
  const pageConfig = getPageConfig();
  const defaultConfig = getDefaultConfig();
  const { loanID = "", sourceEntityID } = useParams();
  const [page, setPage] = useState(1);
  const [loanActivity, setLoanActivity] = useState<any[]>([]);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [kycDocs, setKycDocs] = useState([]);
  const [kycDocDetails, setKycDocDetails] = useState([]);

  useEffect(() => {
    if (window.innerWidth < 640) {
      getLoanActivity(1);
      getKYCData();
      getKYCDetails();
    }
  }, []);

  async function getLoanActivity(page: number) {
    const response = (await LoanApplicationService.getLoanActivity({
      loanApplicationID: loanID,
      token: fetchToken(),
      page,
      pageName
    })) as any;
    if (response.status) {
      if (response.data?.userActivityHistory?.length > 0) {
        setLoanActivity((prevState) => [
          ...prevState,
          ...response?.data?.userActivityHistory
        ]);
      } else {
        setHasMorePages(false);
      }
    } else {
      console.log("API failed", response?.error);
    }
  }

  async function getKYCData() {
    const response = (await LoanApplicationService.getLoanKYC({
      loanApplicationID: loanID,
      token: fetchToken(),
      pageName
    })) as any;

    if (response.status) {
      setKycDocs(response.data?.kycDocs ?? []);
    }
  }

  async function getKYCDetails() {
    const response = (await LoanApplicationService.getKYCDetails({
      loanApplicationID: loanID,
      token: fetchToken(),
      pageName
    })) as any;

    if (response.status) {
      setKycDocDetails(response.data?.documents ?? []);
    }
  }

  useEffect(() => {
    if (page > 1 && hasMorePages) {
      getLoanActivity(page);
    }
  }, [page]);

  function onViewMoreClick() {
    setPage((prevState) => prevState + 1);
  }

  const data = getFormattedSectionData(
    loanDetails,
    sourceEntityID,
    loanDetails.userID,
    userState?.permissions,
    pageConfig?.[pageName],
    defaultConfig?.[pageName]
  );

  const rejectedDocs = [] as any[];
  const approvedDocs = [] as any[];
  kycDocs?.map((doc) =>
    doc?.rejectionReason?.length > 0
      ? rejectedDocs.push(doc)
      : approvedDocs.push(doc)
  );

  const getRuleEnginePre = () => {
    const data = loanDetails?.ruleEnginePredictors ?? [];
    return data?.map((v: any) => {
      return {
        category: "rule-engine",
        name: v?.rule,
        value: v?.value
      };
    });
  };

  return userState?.organizationID === ORG_IDS.MFL_LAP ? (
    <LoanDetailsTab
      loanID={loanID!}
      pageName={pageName || ""}
      canUser={undefined}
      token={fetchToken()}
      pageConfig={pageConfig}
      isLenderDashboard={false}
      loanDetails={loanDetails}
      defaultConfig={defaultConfig}
      sourceEntityID={sourceEntityID!}
      whiteLabelStyle={whiteLabelStyle}
      permissions={userState.permissions ?? {}}
      loanDetailFields={LOAN_DETAIL_FIELDS}
      notify={notify}
      isMFL_LAP={userState?.organizationID === ORG_IDS.MFL_LAP}
      resourceName="details_tab"
      resourceNameSingleSubmit="single_submit_details_tab"
      service="master_dashboard"
    />
  ) : (
    <>
      <div className="hidden md:block md:px-4 md:overflow-auto">
        <DetailsSection
          title={"Loan Details"}
          items={data["loan-details"]}
        />
        <DetailsSection
          title={"Additional Info"}
          items={data["additional-info"]}
        />
        <DetailsSection
          items={data["communication"]}
          title={"Communication Details"}
        />

        {getRuleEnginePre()?.length ? (
          <DetailsSection
            items={getRuleEnginePre()}
            title="Policy Parameters"
          />
        ) : null}
      </div>

      <div className="block md:hidden">
        <Accordion
          defaultOpen={true}
          title="Application Details"
          styles={{
            container: "block border border-gray-200 rounded-lg mb-4",
            button:
              "px-4 py-2 flex items-center justify-between w-full text-sm font-medium"
          }}
        >
          <MobileDetailsSection
            details={loanDetails}
            title={"User Details"}
            levelsData={MOBILE_LOAN_DETAILS_SECTION}
            additionalLinks={MOBILE_LOAN_DETAILS_SECTION_LINKS}
          />
        </Accordion>

        <Accordion
          title="Agent Details"
          styles={{
            container: "block border border-gray-200 rounded-lg mb-4",
            button:
              "px-4 py-2 flex items-center justify-between w-full text-sm font-medium"
          }}
          defaultOpen={false}
        >
          <MobileDetailsSection
            details={loanDetails}
            title={"Agent Details"}
            levelsData={MOBILE_LEADS_AGENT_DETAILS_SECTION}
          />
        </Accordion>

        <Accordion
          defaultOpen={false}
          title="Activity Timeline"
          styles={{
            container: "border border-gray-200 rounded-lg mb-4",
            button:
              "px-4 py-2 flex items-center justify-between w-full text-sm font-medium"
          }}
        >
          <div className="px-4">
            <ActivityTimeline
              page={page}
              timeline={loanActivity}
              hasMorePages={hasMorePages}
              onViewMoreClick={onViewMoreClick}
            />
          </div>
        </Accordion>

        <Accordion
          title="KYC Docs"
          styles={{
            container: "border border-gray-200 rounded-lg mb-4",
            button:
              "px-4 py-2 flex items-center justify-between w-full text-sm font-medium"
          }}
          defaultOpen={false}
        >
          <div className="flex flex-wrap px-4 pt-4">
            <div className="w-full pb-8">
              {!kycDocs?.length ? (
                <div className="text-xs text-gray-400">
                  No documents uploaded yet!
                </div>
              ) : (
                <>
                  <div>
                    <h4 className="text-sm text-gray-700 font-medium w-max border-b-2 border-indigo-400 py-2 pr-2 mb-4">
                      Approved
                    </h4>
                    <div className="flex flex-wrap">
                      {approvedDocs?.length > 0 ? (
                        approvedDocs.map((doc) => {
                          return (
                            <div className="w-1/2 px-1 text-xs flex items-center justify-start mb-4">
                              <CheckmarkIcon className="w-4 h-4 text-gray-400 mr-4" />
                              <span className="truncate w-[calc(100%_-_40px)]">
                                {doc.docType?.replace(/_/g, " ")}
                              </span>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-xs mt-4 text-gray-400">None</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm text-gray-700 font-medium w-max border-b-2 border-indigo-400 py-2 pr-2 mb-4">
                      Rejected
                    </h4>
                    <div className="flex flex-wrap">
                      {rejectedDocs?.length > 0 ? (
                        rejectedDocs?.map((doc) => {
                          return (
                            <div className="w-full px-1 text-xs flex items-center justify-start mb-4">
                              <ErrorIcon className="w-4 h-4 text-red-500 mr-4" />
                              <span className="truncate w-[calc(100%_-_120px)]">
                                {doc?.docType?.replace(/_/g, " ")}
                              </span>
                              <div className="flex items-center">
                                {doc?.frontImagePath && (
                                  <ArrowDownTrayIcon
                                    onClick={() =>
                                      window.open(doc.frontImagePath, "_self")
                                    }
                                    className="w-4 h-4 text-gray-400 mr-4"
                                  />
                                )}
                                {doc?.backImagePath && (
                                  <ArrowDownTrayIcon
                                    onClick={() =>
                                      window.open(doc?.backImagePath, "_self")
                                    }
                                    className="w-4 h-4 text-gray-400 mr-4"
                                  />
                                )}
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-xs mt-4 text-gray-400">None</p>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </Accordion>
      </div>
    </>
  );
}

export default LoanOverview;
